'use client'

import {
  Listbox,
  ListboxOption,
  ListboxOptions,
  Transition,
} from '@headlessui/react'
import s from './select.module.css'
import cx from 'classnames'
import { DropdownButton } from '@/components/v2/atoms/dropdown-button/dropdown-button'
import { XIconNew } from '@/components/svg/x-icon-new/x-icon-new'
import { ArrowRight } from '@/components/svg/arrow-right/arrow-right'
import { useEffect, useState } from 'react'
import { ONE_SECOND_IN_MILLISECONDS } from '@/constants'

type OptionItem = {
  value: number | string
  label: string
}

type OnChange = (value: OptionItem['value']) => void

interface SelectProps<T extends OptionItem> {
  options: T[]
  onChange: (value: T['value']) => void
  onRemove?: (value: T['value']) => void
  label?: string
  value?: T['value']
  disabled?: boolean
  className?: string
  animated?: boolean
}

export const Select = <T extends OptionItem>({
  label,
  options,
  onChange,
  onRemove,
  value,
  disabled,
  className,
  animated,
}: SelectProps<T>) => {
  const [animatedIndex, setAnimatedIndex] = useState(0)

  useEffect(() => {
    if (!animated || value) return

    const interval = setInterval(() => {
      setAnimatedIndex((prev) => (prev + 1) % options.length)
    }, ONE_SECOND_IN_MILLISECONDS * 2.5)

    return () => clearInterval(interval)
  }, [animated, value, options.length])

  const selectedLabel = options.find((o) => o.value === value)?.label

  return (
    <div className={cx(s.wrapper, s.typography, className)}>
      <Listbox onChange={onChange} disabled={disabled} defaultValue={value}>
        {({ open }) => (
          <div>
            <DropdownButton
              label={
                value ? (
                  <span className={cx(s.currentValue, s.typography)}>
                    {selectedLabel}
                    {onRemove ? (
                      <span role="button" onClick={() => onRemove(value)}>
                        <XIconNew />
                      </span>
                    ) : null}
                  </span>
                ) : animated ? (
                  <span className={s.animatedWrapper}>
                    {options.map((o) => (
                      <span
                        key={o.value}
                        className={cx(s.animatedItem, {
                          [s.animatedVisible]:
                            o.value === options[animatedIndex]?.value,
                        })}
                      >
                        {o.label}
                      </span>
                    ))}
                  </span>
                ) : (
                  label
                )
              }
              className={cx(s.button, s.typography)}
              icon={
                <ArrowRight
                  svgProps={{
                    style: {
                      transform: open ? 'rotate(-90deg)' : 'rotate(90deg)',
                      position: 'absolute',
                      right: '12px',
                    },
                  }}
                />
              }
            />
            <Transition
              as="div"
              leave={s.transitionLeave}
              leaveFrom={s.opacity1}
              leaveTo={s.opacity0}
              enterFrom={s.opacity0}
              enterTo={s.opacity1}
            >
              <ListboxOptions className={s.options} static modal={false}>
                {options.map((o) => {
                  return (
                    <ListboxOption
                      key={o.value}
                      value={o.value}
                      className={s.listboxOption}
                    >
                      {({ selected, active }) => {
                        return (
                          <div
                            className={cx(s.option, {
                              [s.selected]: selected,
                              [s.active]: active,
                            })}
                          >
                            <span>{o.label}</span>
                          </div>
                        )
                      }}
                    </ListboxOption>
                  )
                })}
              </ListboxOptions>
            </Transition>
          </div>
        )}
      </Listbox>
    </div>
  )
}
